import type { Schema } from '@dealdesk/api-schema';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { uploadFile } from '@/shared/api';
import { api, unwrap } from '@/shared/api/client';
import { idempotent, ifMatch } from '@/shared/api/headers';

import { dealKeys } from './keys';
import { settlementQuery } from './queries';

/** The settlement item commands (POST …/settlement/items/{itemId}/commands/<command>). */
export type SettlementCommand = 'request_letter' | 'record_letter' | 'request_reissue' | 'mark_letter_used' | 'record_liability' | 'issue_mc' | 'record_settled' | 'record_clearance';

export interface SettlementCommandBody {
  issuedOn?: string;
  expiresOn?: string;
  reference?: string;
  amount?: string;
  date?: string;
  note?: string;
}

/** Version of the settlement plan on screen (If-Match on every settlement command). */
async function planVersion(qc: QueryClient, dealId: string): Promise<string> {
  return (await qc.query({ ...settlementQuery(dealId), staleTime: 'static' })).version;
}

function setPlan(qc: QueryClient, dealId: string, plan: Schema<'SettlementPlan'>) {
  qc.setQueryData(dealKeys.tab(dealId, 'settlement'), plan);
  void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId), exact: true });
  void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'overview') });
  void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'activity') });
  void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'operations') });
  void qc.invalidateQueries({ queryKey: ['ops'] });
}

type CommandHeader = { 'If-Match': string; 'Idempotency-Key': string };

function post(command: SettlementCommand, dealId: string, itemId: string, header: CommandHeader, body: SettlementCommandBody & { evidenceUploadId?: string }) {
  const params = { path: { dealId, itemId }, header };
  switch (command) {
    case 'request_letter':
      return api.POST('/deals/{dealId}/settlement/items/{itemId}/commands/request-letter', { params, body });
    case 'record_letter':
      return api.POST('/deals/{dealId}/settlement/items/{itemId}/commands/record-letter', { params, body: { ...body, issuedOn: body.issuedOn ?? '', expiresOn: body.expiresOn ?? '' } });
    case 'request_reissue':
      return api.POST('/deals/{dealId}/settlement/items/{itemId}/commands/request-reissue', { params, body });
    case 'mark_letter_used':
      return api.POST('/deals/{dealId}/settlement/items/{itemId}/commands/mark-letter-used', { params, body });
    case 'record_liability':
      return api.POST('/deals/{dealId}/settlement/items/{itemId}/commands/record-liability', { params, body: { ...body, amount: body.amount ?? '' } });
    case 'issue_mc':
      return api.POST('/deals/{dealId}/settlement/items/{itemId}/commands/issue-mc', { params, body: { ...body, reference: body.reference ?? '' } });
    case 'record_settled':
      return api.POST('/deals/{dealId}/settlement/items/{itemId}/commands/record-settled', { params, body: { ...body, date: body.date ?? '' } });
    case 'record_clearance':
      return api.POST('/deals/{dealId}/settlement/items/{itemId}/commands/record-clearance', { params, body: { ...body, date: body.date ?? '' } });
  }
}

/**
 * One settlement item command. The server enforces the ordering rules (MC needs an available, unexpired letter;
 * settled needs the MC; clearance needs settlement). A letter / clearance file is uploaded first and sent as an id.
 */
export function useSettlementCommand(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId, command, body, file }: { itemId: string; command: SettlementCommand; body: SettlementCommandBody; file?: File | null }) => {
      const evidenceUploadId = file ? await uploadFile(file, () => undefined, undefined, 'settlement') : undefined;
      const header = { ...ifMatch(await planVersion(qc, dealId)), ...idempotent() };
      return unwrap(post(command, dealId, itemId, header, { ...body, ...(evidenceUploadId ? { evidenceUploadId } : {}) }));
    },
    onSuccess: (plan) => setPlan(qc, dealId, plan),
  });
}

/** Four-eyes shortfall waiver: request (A) → approve or reject (B ≠ A). */
export function useShortfallWaiver(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ step, text }: { step: 'request' | 'approve' | 'reject'; text: string }) => {
      const header = { ...ifMatch(await planVersion(qc, dealId)), ...idempotent() };
      const params = { path: { dealId }, header };
      if (step === 'request') return unwrap(api.POST('/deals/{dealId}/settlement/shortfall/waiver/request', { params, body: { reason: text } }));
      if (step === 'approve') return unwrap(api.POST('/deals/{dealId}/settlement/shortfall/waiver/approve', { params, body: text ? { note: text } : {} }));
      return unwrap(api.POST('/deals/{dealId}/settlement/shortfall/waiver/reject', { params, body: { reason: text } }));
    },
    onSuccess: (plan) => setPlan(qc, dealId, plan),
  });
}
